import type { KeyboardEvent, ReactNode } from "react";
import { cn } from "../../lib/cn";
import { statusRailColor, type AnyStatus } from "./StatusDot";

/**
 * Shared, lean card primitive ("Quiet Rails"). One `--bg-surface` panel + a
 * `--border-subtle` hairline — NO resting shadow. Hover is a neutral
 * `--bg-hover` wash (not a shadow); selection is a quiet accent-soft fill +
 * accent-muted hairline (no ring stacking). Shadow is reserved for things
 * that genuinely float (drag overlay, menus, modals). Tokens only — no raw
 * hex, no nested chrome. The one sanctioned "card" surface (see CLAUDE.md):
 * board cards, stage tiles, and task rows all compose it.
 *
 * Pass `status` to get a 3px per-status left rail — the at-a-glance status
 * cue that ties the board and the Stage together.
 */
interface CardProps {
  children: ReactNode;
  onClick?: () => void;
  selected?: boolean;
  className?: string;
  /** Accessible label when the card is interactive (has an onClick). */
  ariaLabel?: string;
  /** When set, renders a 3px per-status left rail. */
  status?: AnyStatus;
}

export function Card({
  children,
  onClick,
  selected,
  className,
  ariaLabel,
  status,
}: CardProps) {
  const interactive = !!onClick;
  const onKeyDown = interactive
    ? (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }
    : undefined;
  return (
    <div
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? selected : undefined}
      aria-label={ariaLabel}
      className={cn(
        "relative bg-bg-surface rounded-md border transition-all duration-fast ease-out",
        interactive &&
          "cursor-pointer hover:bg-bg-hover hover:border-border-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas",
        selected
          ? "border-accent-muted bg-accent-soft"
          : "border-border-subtle",
        className,
      )}
    >
      {status && (
        <span
          aria-hidden="true"
          className="absolute inset-y-0 left-0 w-[3px] rounded-l-md"
          style={{ background: statusRailColor(status) }}
        />
      )}
      {children}
    </div>
  );
}

/**
 * Compact one-line card row: a leading status dot (color passed as a CSS
 * variable so callers stay token-driven) + main content + optional trailing
 * slot. Used by the task-by-status side panel and anywhere a dense, clickable
 * status row is wanted.
 */
export function StatusRow({
  dotColor,
  children,
  trailing,
  onClick,
  selected,
  pulse,
  className,
  ariaLabel,
}: {
  dotColor: string;
  children: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  selected?: boolean;
  pulse?: boolean;
  className?: string;
  ariaLabel?: string;
}) {
  return (
    <Card
      onClick={onClick}
      selected={selected}
      ariaLabel={ariaLabel}
      className={cn("px-2.5 py-2", className)}
    >
      <div className="flex items-center gap-2">
        <span className="relative inline-flex h-2 w-2 shrink-0">
          {pulse && (
            <span
              className="absolute inset-0 rounded-full motion-safe:animate-ping"
              style={{ background: dotColor, opacity: 0.45 }}
            />
          )}
          <span
            className="relative inline-block h-2 w-2 rounded-full"
            style={{ background: dotColor }}
          />
        </span>
        <div className="min-w-0 flex-1">{children}</div>
        {trailing && <div className="shrink-0">{trailing}</div>}
      </div>
    </Card>
  );
}

/**
 * Small inset chip for metadata (agent type, branch name, tool). Shared so
 * stage tiles, task rows and board cards render identical chips.
 */
export function Chip({
  children,
  title,
  className,
}: {
  children: ReactNode;
  title?: string;
  className?: string;
}) {
  return (
    <span
      title={title}
      className={cn(
        "inline-flex items-center h-[18px] px-1.5 rounded-sm max-w-full",
        "bg-bg-inset text-mono text-text-tertiary truncate",
        className,
      )}
    >
      {children}
    </span>
  );
}
